import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { isManagerOrAdmin } from 'shared/domain/role/isManagerOrAdmin';
import { isVisibleNoAccess } from 'shared/domain/role/isVisibleNoAccess';
import {
  LabelledUser,
  LabelledUserWithSitesAndProcesses,
} from 'shared/domain/user/mapping/toView';
import { UserRole } from 'shared/types/userRole';
import { normalizeForSearch } from 'shared/utils/search';
import type { AccessWeight } from 'shared/domain/issue/mapping/accessLevel/model/types';
import type { AccessManagementRow } from './accessManagementRow';

export function calculateUsersToDisplay(
  userAccesses: UserAccess[],
  usersWithPotentialAccess: LabelledUserWithSitesAndProcesses[],
  phrase: string
): Map<string, LabelledUserWithSitesAndProcesses> {
  const usersMap = new Map(
    usersWithPotentialAccess.map((user) => [user._id, user])
  );
  const filteredUsersMap = new Map();

  usersMap.forEach((user) => {
    if (
      !hasGrantedReadOrEditAccess(userAccesses, user._id) &&
      includesPhrase(user, phrase)
    ) {
      filteredUsersMap.set(user._id, user);
    }
  });

  userAccesses.forEach((access) => {
    const user = usersMap.get(access.accessingUser);
    if (!access.deleted && user && includesPhrase(user, phrase)) {
      filteredUsersMap.set(access.accessingUser, user);
    }
  });

  return filteredUsersMap;
}

function hasGrantedReadOrEditAccess(
  userAccesses: UserAccess[],
  userId: string
): boolean {
  return Boolean(
    userAccesses.find(
      (access) => access.accessingUser === userId && !access.deleted
    )
  );
}

function includesPhrase(user: LabelledUser, phrase: string): boolean {
  return (
    normalizeForSearch(user.email).includes(phrase) ||
    normalizeForSearch(user.label).includes(phrase)
  );
}

const ACCESS_WEIGHT: AccessWeight = {
  [AccessLevelModel.NONE]: 0,
  [AccessLevelModel.READ]: 1,
  [AccessLevelModel.EDIT]: 2,
};

export function rowSort(
  a: AccessManagementRow,
  b: AccessManagementRow
): number {
  return (
    ACCESS_WEIGHT[b.accessLevel.value] -
      ACCESS_WEIGHT[a.accessLevel.value] ||
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
  );
}
